import {
  LinxAccountType,
  LinxCounterpartyParent,
  LinxEnhanceAccountType,
  LinxEntityType,
  LinxNotificationChannel,
  LinxProfileType,
  LinxSteps,
  PaymentFrequency,
  TransactionOccurrenceType,
} from '@shared/constants';
import { AttachmentCreateModel } from '@shared/models/attachments';
import { CustomerList } from '@shared/models/customer';

import { FinancialAccountListItem } from '../financial-account';
import { IdentityVerificationProfile } from '../identity-verification-application';
import { PagingProps } from '../paging';
import { MoveHowAchDetails, MoveHowWireDetails, MoveWireDetails, TransactionSettlementPriority, TransactionSolution } from '../transaction';
import { CreatedUpdatedBy } from '../user';

export type LinxRequestRowType =
  | 'existing-financial-account'
  | 'enhance-profile'
  | 'financial-account'
  | 'document-acceptance'
  | 'debit-authorization'
  | 'move-money'
  | 'enhance-beneficiary'
  | 'identity-verification';

export interface LinxCustomer {
  id?: string;
  customerType?: LinxAccountType;
  profileType?: LinxProfileType;
  phone?: string;
  email?: string;
  name?: string;
}

export interface LinxCounterparty {
  id?: string;
  type: LinxAccountType;
  parent?: LinxCounterpartyParent;
  phone?: string;
  email?: string;
  name?: string;
}

export interface LinxEntityModalData {
  entityId?: string;
  phone?: string;
  email?: string;
  notificationEmail?: string;
  notificationPhone?: string;
  name?: string;
  entityType: LinxEntityType;
  profileType: LinxProfileType;
  accountType?: LinxAccountType;
  linxCustomer?: LinxCustomer;
  workflowTitle?: string;
  templateName?: string;
  counterpartyParent?: LinxCounterpartyParent;
  customer?: CustomerList;
  notificationChannel?: LinxNotificationChannel;
  verificationStatus?: CustomerVerificationStatus;
}

export type CustomerVerificationStatus = 'UNVERIFIED' | 'PROCESSING' | 'APPROVED' | 'DECLINED' | 'RESTRICTED' | 'REVIEW';

export interface LinxRequestOptionType {
  type: LinxRequestRowType;
  label: string;
  iconName: string;
  isChecked?: boolean;
  hasValue?: boolean;
  disabled?: boolean;
}

export interface LinxRequestFinancialAccount {
  financialAccountType?: LinxRequestFinancialAccountType;
  financialAccountSubType?: string;
  existingFinancialAccount?: FinancialAccountListItem;
  verifyFinancialAccount?: boolean;
  debitAuthorization?: LinxRequestDebitAuthorizationDetails;
}

export enum LinxRequestFinancialAccountType {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
  NEW_OR_EXISTING = 'NEW_OR_EXISTING',
}

export interface LinxRequestDocumentDetails {
  documentGroup: string;
  documentGroupTitle?: string;
  documentId: string;
  documentTitle?: string;
}

export interface LinxRequestDebitAuthorizationDetails {
  authorization?: 'MANUAL' | 'OPEN';
  businessName?: string;
  fixedTerm?: number;
  fixedAmount?: string;
  amount?: string;
  occurrenceType?: TransactionOccurrenceType;
  timeZone?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  paymentFrequency?: PaymentFrequency;
  currency?: string;
  debitAuthorizationId?: string;
  offlineAuthorization?: boolean;
  debitAuthorizationType?: 'NEW' | 'EXISTING' | 'OFFLINE';
  // Scheduler fields for ACH Request
  isScheduled?: boolean;
  scheduleRecurrenceRule?: string;
  scheduleName?: string;
  scheduleStartDateTime?: string;
}

export interface LinxMoveMoneyScheduleDetails {
  scheduleDateTime?: string;
  scheduleDateTimeDisplayValue?: string;
  scheduleRecurringDisplayValue?: string;
  recurrenceRule?: string;
  timeZone?: string;
  name?: string;
}

export interface LinxRequestMoveMoneyDetails {
  selectedMoveItem?: string;
  selectedMoveHowType?: TransactionSolution;
  paymentReasonName?: string;
  paymentReasonId?: string;
  moveAmountValue?: string;
  selectedMoveFrom?: FinancialAccountListItem;
  selectedMoveFromId?: string;
  note?: string;
  achDetails?: MoveHowAchDetails;
  moveHowDirectionDisplayValue?: string;
  moveHowSolutionDisplayValue?: string;
  moveHowSolutionIcon?: string;
  achEntryTypeDisplayName?: string;
  moveAchEntryType?: string;
  moveHowAchDetails?: MoveHowAchDetails;
  moveHowWireDetails?: MoveHowWireDetails;
  wireDetails?: MoveWireDetails;
  financialAccountDetails?: LinxRequestFinancialAccount;
  scheduleDetails?: LinxMoveMoneyScheduleDetails;
}

export interface LinxReviewModalData {
  entityModalData?: LinxEntityModalData;
  expiryDays?: number;
  expiryInHours?: number;
  customerMemo?: string;
  enhanceAccountDetails?: LinxEnhanceAccountDetails;
  enhanceProfileDisplayData: string[];
  enhanceBeneficiaryDetails?: LinxRequestBenificiaryDetails;
  enhanceBeneficiaryDisplayData?: string[];
  documentAcceptanceDetails?: LinxRequestDocumentDetails[];
  moveMoneyDetails?: LinxRequestMoveMoneyDetails;
  identityVerificationDetails?: IdentityVerificationProfile;
  financialAccountDetails?: LinxRequestFinancialAccount;
  attachments?: AttachmentCreateModel[];
  debitAuthorizationDetails?: LinxRequestDebitAuthorizationDetails;
}

export interface LinxRequestList extends PagingProps {
  content: LinxRequestListItem[];
}
export interface LinxRequestListItem {
  id: string;
  status: string;
  title: string;
  expireAt: string;
  phoneNumber: string;
  email: string;
  batchId?: string;
}

export interface LinxRequestPayload {
  expireIn?: number;
  expiryInHours?: number;
  title?: string;
  parameters?: LinxRequestParameters;
  steps?: LinxRequestStepPayload[];
  templateName?: string;
  termplateId?: string;
  customerMemo?: string;
}

export interface LinxRequestParameters {
  channel?: string;
  phoneNumber?: string;
  email?: string;
  customerId?: string;
  counterpartyId?: string;
  transactionType?: string;
  solution?: string;
  priority?: string;
  originatorFinancialAccountId?: string;
  currency?: string;
  amount?: string;
  paymentReasonId?: string;
  counterPartyFinancialAccountId?: string;
  financialAccountSubType?: string;
  /** select-country: iso3 codes the step offers. KOR rejects an empty array, so omit rather than send []. */
  allowedCountryCodes?: string[];
  description?: string;
  memo?: string;
  rkorACHIndividualId?: string;
  beneficiaryIds?: string[];
  idvProfile?: string;
  occurrenceType?: TransactionOccurrenceType;
  paymentFrequency?: PaymentFrequency;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  debitAuthorizationId?: string;
  scheduleName?: string;
  scheduleStartDateTime?: string;
  scheduleRecurrenceRule?: string;
  isScheduled?: boolean;
  bypassDebitAuthorization?: boolean;
}

export interface LinxRequestStepPayload {
  name?: string;
  inputs?: {
    user?: LinxRequestUserFields;
  };
}

export interface LinxRequestUserFields extends LinxEnhanceProfileDetails {
  documents?: LinxRequestDocumentDetails[];
  accountType?: LinxEnhanceAccountType;
  documentTypes?: string[];
  otherDocuments?: LinxOtherDocument[];
  counterpartyType?: LinxAccountType;
  verifyFinancialAccount?: boolean;
  offlineAuthorization?: boolean;
}

export interface LinxOtherDocument {
  name: string;
  description?: string;
}

export interface LinxRequestDetails {
  id?: string;
  templateId?: string;
  batchId?: string;
  status?: string;
  title?: string;
  currentStep?: number;
  steps?: LinxRequestDetailsStep[];
  phoneNumber?: string;
  email?: string;
  expireAt?: string;
  createdBy: CreatedUpdatedBy;
  createdAt: string;
  updatedBy: CreatedUpdatedBy;
  updatedAt: string;
  declineReason?: string;
  declineDescription?: string;
  notifications?: LinxRequestNotification[];
  customerMemo?: string;
}

export interface LinxRequestNotification {
  serial: string;
  channel: string;
  notifiedBy: CreatedUpdatedBy;
  notifiedByUser?: string;
  notifiedAt: string;
}

/**
 * How complete a COMPLETED step really is, once the obligations it opened are accounted for.
 * Absent unless `status` is COMPLETED, which it refines rather than replaces (KOR-15038).
 */
export type LinxStepCompletionState = 'COMPLETED' | 'AWAITING_GATE' | 'GATE_FAILED' | 'GATE_ABANDONED';

export interface LinxRequestDetailsStep {
  stepName: LinxSteps;
  stepDisplayName: string;
  stepDescription: string;
  data: LinxRequestDetailsStepData;
  input?: {
    user?: LinxRequestUserFields;
  };
  status: string;
  /**
   * Set only on a COMPLETED step. An e-signature step reports COMPLETED the moment the primary
   * signs - so the customer is not held up by the other parties - and carries AWAITING_GATE here
   * until every party has signed. Optional: older responses omit it entirely.
   */
  completionState?: LinxStepCompletionState;
}

export interface LinxRequestDetailsStepData {
  customerId: string;
  status?: CustomerVerificationStatus;
  email: string;
  phoneNumber: string;
  customerFinancialAccountName: string;
  transactionType: string;
  amount: string;
  solution: TransactionSolution;
  currency: string;
  priority: TransactionSettlementPriority;
  paymentReasonId: string;
  baFinancialAccountName: string;
  transactionId: string;
  financialAccountSubType: string;
  customerFinancialAccountId: string;
  user?: LinxRequestUserFields;
  documents?: LinxRequestDocumentDetails[];
  idvProfile?: string;
  occurrenceType?: TransactionOccurrenceType;
  paymentFrequency?: PaymentFrequency;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  scheduleName?: string;
  scheduleStartDateTime?: string;
  scheduleRecurrenceRule?: string;
  originatorFinancialAccountName?: string;
}

export interface LinxDocuments {
  documentGroup: string;
  documentGroupTitle: string;
  documents: LinxDocument[];
}

export interface LinxDocument {
  id: string;
  title: string;
  versions: LinxDocumentVersions[];
}

export interface LinxDocumentVersions {
  version: string;
  createdAt: string;
  isCurrentVersion: boolean;
  isRequiredMinimumVersion: boolean;
}

export interface LinxRequestCreateResponse {
  workflowId: string;
  templateId?: string;
  idempotencyKey: string;
  result: {
    successful: boolean;
  };
}

export interface LinxModalActionText {
  action: string;
  actionName: string;
  type: string;
}

export interface LinxRequestExtendExpiry {
  extendBy?: number;
}

export type LinxRequestDetailsAction = 'UPDATE_EXPIRY' | 'RESEND_NOTIFICATION' | 'CANCEL_REQUEST';

export interface LinxEnhanceAccountDetails {
  accountType?: LinxEnhanceAccountType;
  enhanceProfileDetails?: LinxEnhanceProfileDetails;
  documentTypes?: string[];
  otherDocuments?: LinxOtherDocument[];
}

export interface LinxRequestBenificiaryDetails {
  beneficiaryType: LinxProfileType;
  beneficiaryIds?: string[];
  enhanceProfileDetails?: LinxEnhanceProfileDetails;
  documentTypes?: string[];
  otherDocuments?: LinxOtherDocument[];
  email?: string[];
}

export interface LinxEnhanceProfileDetails {
  personalInfo?: string[];
  contacts?: string[];
  addresses?: string[];
  businessInfo?: string[];
  phoneNumber?: string[];
  email?: string[];
}
